// src/pages/Catalog/ProductCard.js
import React, { useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Typography,
    IconButton,
    Chip,
    Box,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { AuthContext } from "../../context/Auth/AuthContext";

// Inside your ProductModal component, add this state:


const useStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[4],
        },
    },
    media: {
        height: 200,
        backgroundSize: 'contain',
    },
    navigationArrow: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
        },
    },
    leftArrow: {
        left: theme.spacing(1),
    },
    rightArrow: {
        right: theme.spacing(1),
    },
    content: {
        flexGrow: 1,
    },
    price: {
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        fontSize: '1.2rem',
        marginTop: theme.spacing(1),
    },
    attributes: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    actions: {
        justifyContent: 'flex-end',
        paddingTop: 0,
    },
    noImage: {
        height: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[500],
    },
}));

const ProductCard = ({ product, onEdit }) => {
    const classes = useStyles();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [currentImageIndexes, setCurrentImageIndexes] = useState({});
    const { user } = useContext(AuthContext);


    const handleDelete = async () => {
        try {
            await api.delete(`/products/${product.id}`);
            toast.success("Producto eliminado con éxito");
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };
    const handlePrevImage = (productId) => {
        setCurrentImageIndexes(prev => ({
            ...prev,
            [productId]: Math.max((prev[productId] || 0) - 1, 0)
        }));
    };

    const handleNextImage = (productId) => {
        setCurrentImageIndexes(prev => ({
            ...prev,
            [productId]: Math.min((prev[productId] || 0) + 1, product.images.length - 1)
        }));
    };


    const mainImage = product.images?.find(img => img.mainImage) || product.images?.[0];

    return (
        <>
            <Card className={classes.root} onClick={onEdit}>
                {product.images && product.images.length > 0 ? (
                    <div style={{ position: 'relative' }}>
                        <CardMedia
                            className={classes.media}
                            image={`${process.env.REACT_APP_BACKEND_URL}public/products/${product.images[currentImageIndexes[product.id] || 0].imageUrl}`}
                            title={product.name}
                        />
                        {product.images.length > 1 && (
                            <>
                                <IconButton
                                    className={`${classes.navigationArrow} ${classes.leftArrow}`}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePrevImage(product.id);
                                    }}
                                    disabled={(currentImageIndexes[product.id] || 0) === 0}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                                <IconButton
                                    className={`${classes.navigationArrow} ${classes.rightArrow}`}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNextImage(product.id);
                                    }}
                                    disabled={(currentImageIndexes[product.id] || 0) === product.images.length - 1}
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                            </>
                        )}
                    </div>
                ) : (
                    <div className={classes.noImage}>
                        <Typography variant="body2">Sin imagen</Typography>
                    </div>
                )}
                <CardContent className={classes.content}>
                    <Typography gutterBottom variant="h6" component="h2">
                        {product.name}
                        {product.category && (
                            <Chip
                                size="small"
                                label={product.category.name}
                                color="primary"
                                variant="outlined"
                                style={{ marginTop: 4, marginBottom: 4 }}
                            />
                        )}
                    </Typography>
                    <Typography variant="body2">ID: {product.id}</Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        component="p"
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {product.description || "Sin descripción"}
                    </Typography>
                    <Typography className={classes.price}>
                        {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: product.currency || 'USD'
                        }).format(product.price)}
                    </Typography>
                    {product.attributes && product.attributes.length > 0 && (
                        <Box className={classes.attributes}>
                            {product.attributes.slice(0, 3).map((attr, index) => (
                                <Chip
                                    key={index}
                                    label={`${attr.name}: ${attr.value}`}
                                    size="small"
                                    className={classes.chip}
                                />
                            ))}
                            {product.attributes.length > 3 && (
                                <Chip
                                    label={`+${product.attributes.length - 3} más`}
                                    size="small"
                                    variant="outlined"
                                    className={classes.chip}
                                />
                            )}
                        </Box>
                    )}
                </CardContent>
                {user?.profile === "admin" && (
                    <CardActions className={classes.actions}>
                        <IconButton
                            size="small"
                            onClick={onEdit}
                            title="Editar producto"
                        >
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setConfirmModalOpen(true);
                            }}
                            title="Eliminar producto"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </CardActions>
                )}

            </Card>
            <DeleteConfirmationModal
                title="Eliminar Producto"
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleDelete}
                message="¿Estás seguro de que deseas eliminar este producto? Esta acción no se puede deshacer."
            />
        </>
    );
};

export default ProductCard;